import { useContext, useMemo } from "react";
import { AppContext } from "../../contextProviders/AppContext";
import { translation } from "../Dictionary";
import { addHours, format, isValid, startOfDay } from "date-fns";
import { Divider, Box, Typography, Stack } from "@mui/material";
import TimePickerComponent from "./TimePickerComponent";
import FixedReturnTimePickerComponent from "./FixedReturnTimePickerPopper";
import { useTheme } from "@emotion/react";
import { fi } from "date-fns/locale";
import FixedStartTimePicker from "./FixedStartTimePicker";

export default function SelectTime(props) {
  const { contentHeigth, companyOpenHours, companyOpenHoursReturn } = props;
  const {
    mobileViewPort,
    selectedDateRange,
    setSelectedDateRange,
    selectedPicker,
    setSelectedPicker,
    startDateString,
    endDateString,
    FEATURE_FIXED_RESERVATION_DURATION,
    FEATURE_PREDETERMINED_RESERVATION_RETURNTIME,
    FEATURE_OPTIONAL_FIXED_RESERVATION_DURATION,
    groups,
    language,
    defaultTimeFrame,
    fixedTimeFrames,
    customDatePickerType,
    selectedTimeFrame,
  } = useContext(AppContext);
  // TimePicker control
  const theme = useTheme();

  const dayStart = startOfDay(new Date());

  const startTimeLabel = useMemo(() => {
    const group = groups?.[0];
    if (group === "gnista") {
      return translation.get(language, "Timepicker start title accommodation");
    } else {
      return translation.get(language, "Timepicker start title");
    }
  }, [groups, language]);

  const returnTimeLabel = useMemo(() => {
    const group = groups?.[0];
    if (group === "gnista") {
      return translation.get(language, "Timepicker return title accommodation");
    } else {
      return translation.get(language, "Timepicker return title");
    }
  }, [groups, language]);

  const setStartTime = (value) => {
    setSelectedDateRange((prevState) => [value, prevState[1]]);
  };
  const setEndTime = (value) => {
    setSelectedDateRange((prevState) => [prevState[0], value]);
  };

  if (mobileViewPort) return null;

  if (FEATURE_OPTIONAL_FIXED_RESERVATION_DURATION && !customDatePickerType) {
    return (
      <Stack direction={"row"} sx={stackStyles} spacing={2}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <FixedStartTimePicker
            type={"startTime"}
            selectedPicker={selectedPicker}
            setSelectedPicker={setSelectedPicker}
          />
        </Box>

        <Divider
          orientation="vertical"
          variant="middle"
          flexItem
          sx={{ marginY: "10px" }}
        />
        <Stack
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100px",
          }}
        >
          <Typography color={theme.palette.primary.mainText}>
            {translation.get(language, "Return time text")}
          </Typography>
          <Typography color={theme.palette.primary.mainText}>
            {isValid(selectedDateRange[1]) &&
              format(selectedDateRange[1], "HH:mm", {
                locale: fi,
              })}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  if (FEATURE_PREDETERMINED_RESERVATION_RETURNTIME) {
    return (
      <Stack direction={"row"} sx={stackStyles} spacing={1}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <TimePickerComponent
            contentHeigth={contentHeigth}
            selectedPicker={selectedPicker}
            setSelectedPicker={setSelectedPicker}
            type={"startTime"}
            companyOpenHours={companyOpenHours}
            companyOpenHoursReturn={companyOpenHoursReturn}
            selectedDateRange={selectedDateRange}
            title={startTimeLabel}
            dateString={startDateString}
            dayStart={dayStart}
            onChange={(value) => {
              setStartTime(value);
              setEndTime(addHours(value, selectedTimeFrame));
            }}
          />
        </Box>

        <Divider
          orientation="vertical"
          variant="middle"
          flexItem
          sx={{ marginY: "10px" }}
        />
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <FixedReturnTimePickerComponent
            contentHeigth={contentHeigth}
            selectedPicker={selectedPicker}
            setSelectedPicker={setSelectedPicker}
            fixedTimeFrames={fixedTimeFrames}
            defaultTimeFrame={defaultTimeFrame}
            setOpen={setSelectedPicker}
            title={returnTimeLabel}
            type={"returnTime"}
            dateString={endDateString}
            dayStart={dayStart}
            selectedTimeFrame={selectedTimeFrame}
          />
        </Box>
      </Stack>
    );
  }

  // Immutable return time
  if (FEATURE_FIXED_RESERVATION_DURATION) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          backgroundColor: "white",
          borderRadius: "10px",
          transition: "ease-in-out 250ms",
          bgcolor: "transparent",
        }}
      >
        <Typography color={theme.palette.primary.mainText}>
          {translation.get(language, "End reservation text")}
        </Typography>
        <Typography color={theme.palette.primary.mainText}>
          {isValid(selectedDateRange[1]) &&
            format(selectedDateRange[1], "dd.MM.yyyy", {
              locale: fi,
            })}
        </Typography>
      </Box>
    );
  }

  // default time picker with no features
  return (
    <Stack
      direction={"row"}
      sx={{
        ...stackStyles,
        maxWidth: "200px",
      }}
      spacing={1}
    >
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <TimePickerComponent
          type={"startTime"}
          dateString={startDateString}
          contentHeigth={contentHeigth}
          selectedPicker={selectedPicker}
          companyOpenHours={companyOpenHours}
          companyOpenHoursReturn={companyOpenHoursReturn}
          setSelectedPicker={setSelectedPicker}
          title={startTimeLabel}
          selectedTimeFrame={selectedTimeFrame}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <TimePickerComponent
          contentHeigth={contentHeigth}
          selectedPicker={selectedPicker}
          setSelectedPicker={setSelectedPicker}
          companyOpenHours={companyOpenHours}
          companyOpenHoursReturn={companyOpenHoursReturn}
          selectedDateRange={selectedDateRange}
          type={"returnTime"}
          title={returnTimeLabel}
          dateString={endDateString}
          dayStart={dayStart}
          selectedTimeFrame={selectedTimeFrame}
        />
      </Box>
    </Stack>
  );
}
const stackStyles = {
  justifyContent: "space-evenly",
  height: "54px",
  flex: 1,

  alignItems: "center",
};
